import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Search, Users, X } from "lucide-react";
import {
  assignEmployeeSalary,
  listEmployeeSalaryStructures,
} from "../../../SalaryApi";
import { useEmployees } from "../../../../../hooks/useEmployees";
import { findEmployeeById, formatEmployeeLabel } from "../../../../../components/common/employeeUtils";
import { useToast } from "../../../../Context/ToastContext";
import ConfirmDialog from "../../../../../components/ui/ConfirmDialog";
import { formatInr } from "./pipelineCtcUtils";

function employeeDept(emp) {
  return emp?.department?.name || emp?.department_name || "—";
}

function employeeDesignation(emp) {
  return emp?.designation?.name || emp?.designation_name || emp?.job_title || "—";
}

function isActiveEmployee(emp) {
  if (emp?.is_active === false) return false;
  const status = String(emp?.employment_status || emp?.status || "").toLowerCase();
  if (status === "inactive" || status === "terminated" || status === "left") return false;
  return true;
}

export default function TemplateEmployeeManageModal({ templateId, templateName, onClose, onChanged }) {
  const toast = useToast();
  const { employees, loading: employeesLoading, refresh: refreshEmployees } = useEmployees();
  const [structures, setStructures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [bulkCtc, setBulkCtc] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [reassignTarget, setReassignTarget] = useState(null);

  const loadStructures = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await listEmployeeSalaryStructures();
      const list = Array.isArray(rows) ? rows : [];
      setStructures(list.filter((r) => String(r.template_id) === String(templateId)));
    } catch {
      toast.error("Failed to load employee assignments");
      setStructures([]);
    } finally {
      setLoading(false);
    }
  }, [templateId, toast]);

  useEffect(() => {
    refreshEmployees();
    loadStructures();
  }, [loadStructures, refreshEmployees]);

  const assignedIds = useMemo(
    () => new Set(structures.map((s) => String(s.employee_id))),
    [structures]
  );

  const filteredEmployees = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = (employees || []).filter((emp) => {
      const id = String(emp?.employee_id || emp?.id || "");
      if (!id) return false;
      if (assignedIds.has(id)) return true;
      return true;
    });
    if (q) {
      list = list.filter((emp) => {
        const label = formatEmployeeLabel(emp).toLowerCase();
        const code = String(emp?.employee_code || "").toLowerCase();
        return label.includes(q) || code.includes(q) || employeeDept(emp).toLowerCase().includes(q);
      });
    }
    return list;
  }, [employees, search, assignedIds]);

  const stats = useMemo(() => {
    let active = 0;
    let inactive = 0;
    structures.forEach((s) => {
      const emp = findEmployeeById(employees, s.employee_id);
      if (isActiveEmployee(emp)) active += 1;
      else inactive += 1;
    });
    return { total: structures.length, active, inactive };
  }, [structures, employees]);

  function toggleSelect(id) {
    const sid = String(id);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid);
      else next.add(sid);
      return next;
    });
  }

  async function bulkAssign() {
    const ctc = Number(String(bulkCtc || "").replace(/,/g, ""));
    if (!selected.size) {
      toast.error("Select at least one employee.");
      return;
    }
    if (!Number.isFinite(ctc) || ctc <= 0) {
      toast.error("Enter a valid annual gross salary for bulk assign.");
      return;
    }
    setAssigning(true);
    const today = new Date().toISOString().slice(0, 10);
    let ok = 0;
    let failed = 0;
    for (const employeeId of selected) {
      if (assignedIds.has(String(employeeId))) continue;
      try {
        // eslint-disable-next-line no-await-in-loop
        await assignEmployeeSalary({
          employee_id: employeeId,
          template_id: templateId,
          ctc,
          effective_from: today,
        });
        ok += 1;
      } catch {
        failed += 1;
      }
    }
    setAssigning(false);
    setSelected(new Set());
    setBulkCtc("");
    await loadStructures();
    onChanged?.();
    if (failed) toast.error(`${ok} assigned; ${failed} failed.`);
    else toast.success(`${ok} employee${ok === 1 ? "" : "s"} assigned.`);
  }

  return (
    <div className="stp-modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="stp-modal stp-emp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="stp-modal-head">
          <div className="stp-modal-head-title">
            <span className="stp-modal-icon"><Users size={18} /></span>
            <div>
              <h3>Manage employees — {templateName || "Salary structure"}</h3>
              <p>
                {stats.total} assigned · {stats.active} active · {stats.inactive} inactive
              </p>
            </div>
          </div>
          <button type="button" className="stp-icon-btn" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="stp-modal-body stp-emp-modal-body">
          <div className="stp-emp-toolbar">
            <div className="stp-search">
              <Search size={15} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search employees…"
                aria-label="Search employees"
              />
            </div>
            <Link
              className="stp-btn stp-btn-ghost"
              to={`/employeesalarystructure?template_id=${encodeURIComponent(templateId)}`}
              onClick={onClose}
            >
              Open full allocation screen
            </Link>
          </div>

          {selected.size > 0 ? (
            <div className="stp-emp-bulk">
              <span>{selected.size} selected</span>
              <input
                className="stp-input"
                placeholder="Annual gross salary"
                value={bulkCtc}
                onChange={(e) => setBulkCtc(e.target.value)}
                aria-label="Bulk annual gross salary"
              />
              <button
                type="button"
                className="stp-btn stp-btn-primary"
                disabled={assigning}
                onClick={bulkAssign}
              >
                {assigning ? "Assigning…" : "Assign to structure"}
              </button>
            </div>
          ) : null}

          {loading || employeesLoading ? (
            <p className="stp-section-hint"><Loader2 size={16} className="stp-spin" /> Loading…</p>
          ) : (
            <div className="stp-emp-table-wrap">
              <table className="stp-review-table stp-emp-table">
                <thead>
                  <tr>
                    <th aria-label="Select" />
                    <th>Employee</th>
                    <th>Department</th>
                    <th>Designation</th>
                    <th>Annual gross salary</th>
                    <th>Monthly</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEmployees.map((emp) => {
                    const id = String(emp.employee_id || emp.id);
                    const row = structures.find((s) => String(s.employee_id) === id);
                    const assigned = Boolean(row);
                    const annual = row ? Number(row.ctc) : null;
                    const monthly = annual != null && Number.isFinite(annual) ? Math.round(annual / 12) : null;
                    return (
                      <tr key={id}>
                        <td>
                          {!assigned ? (
                            <input
                              type="checkbox"
                              checked={selected.has(id)}
                              onChange={() => toggleSelect(id)}
                              aria-label={`Select ${formatEmployeeLabel(emp)}`}
                            />
                          ) : null}
                        </td>
                        <td>
                          <div className="stp-emp-name">{formatEmployeeLabel(emp)}</div>
                          <div className="stp-section-hint">{emp.employee_code || "—"}</div>
                        </td>
                        <td>{employeeDept(emp)}</td>
                        <td>{employeeDesignation(emp)}</td>
                        <td>{assigned ? formatInr(annual) : "—"}</td>
                        <td>{monthly != null ? formatInr(monthly) : "—"}</td>
                        <td>{isActiveEmployee(emp) ? "Active" : "Inactive"}</td>
                        <td>
                          {assigned ? (
                            <button
                              type="button"
                              className="stp-btn stp-btn-ghost"
                              onClick={() =>
                                setReassignTarget({
                                  employee_id: id,
                                  ctc: row.ctc,
                                  label: formatEmployeeLabel(emp),
                                })
                              }
                            >
                              Edit package
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="stp-btn stp-btn-ghost"
                              onClick={() => {
                                toggleSelect(id);
                                window.scrollTo({ top: 0, behavior: "smooth" });
                              }}
                            >
                              Assign
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {!filteredEmployees.length ? (
                    <tr>
                      <td colSpan={8}>No employees match your search.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={Boolean(reassignTarget)}
        title="Update employee package?"
        message={
          reassignTarget
            ? `Open the full allocation form to change gross salary and overrides for ${reassignTarget.label}.`
            : ""
        }
        confirmLabel="Continue"
        onConfirm={() => {
          if (reassignTarget) {
            window.location.href = `/employeesalarystructure?employee_id=${encodeURIComponent(
              reassignTarget.employee_id
            )}&template_id=${encodeURIComponent(templateId)}`;
          }
          setReassignTarget(null);
          onClose?.();
        }}
        onCancel={() => setReassignTarget(null)}
      />
    </div>
  );
}
